import { ReactNode, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import BowlIcon from 'components/common/icons/BowlIcon'
import CartIcon from 'components/common/icons/CartIcon'
import TabGroup, {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
} from 'components/common/TabGroup'
import { AnimatePresence, motion } from 'motion/react'
import { useScrollDirection } from 'hooks/general'
import { useVariantByScreenSize } from 'hooks/variantByScreenSize'
import StickyHeader from './StickyHeader'
import { track } from 'utils/analytics'
import { AnalyticsEvent, events } from 'analytics/events'
import { MenuHeaderWrapper } from './MenuHeader'

type Tab = 'extras' | 'meals'

const TAB_ICONS: Record<Tab, JSX.Element> = {
  extras: <CartIcon />,
  meals: <BowlIcon />,
}

const TAB_CHANGE_EVENTS: Record<Tab, AnalyticsEvent> = {
  extras: events.TAPPED_MENU_EXTRAS_TAB,
  meals: events.TAPPED_MENU_MEALS_TAB,
}

const MenuTabGroup = ({
  commonMenuContent,
  mealsSort,
  menuHeader,
  menuTabPanels,
  menuTabs,
  tabChangeEventProps,
  totalListingSelections,
}: {
  commonMenuContent?: ReactNode
  mealsSort?: ReactNode
  menuHeader(opts: { isHeaderSticky: boolean }): ReactNode
  menuTabPanels: Record<Tab, ReactNode>
  menuTabs: MenuTabs
  tabChangeEventProps: {
    maxSelections: number
    selectionsCount: number
    termID: number
  }
  totalListingSelections?: number
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  const containerWidth = containerRef.current
    ? containerRef.current.offsetWidth + 32 // plus some padding
    : 0

  const { selectTab, selectedIndex, tabs } = menuTabs

  return (
    <TabGroup
      onChange={(index) => {
        const tab = tabs[index]
        selectTab(tab)

        track(TAB_CHANGE_EVENTS[tab], tabChangeEventProps)
      }}
      selectedIndex={selectedIndex}
    >
      <StickyHeader>
        {({ isHeaderSticky }) => {
          return (
            <div className="relative">
              <div className="mx-auto max-w-menu">
                <MenuHeaderWrapper isHeaderSticky={isHeaderSticky}>
                  {menuHeader({ isHeaderSticky })}
                </MenuHeaderWrapper>
              </div>

              <AnimatedTabList
                isHeaderSticky={isHeaderSticky}
                menuTabs={menuTabs}
                totalListingSelections={totalListingSelections}
              />
            </div>
          )
        }}
      </StickyHeader>

      <div className="pt-[56px]">
        <div>
          {tabs[selectedIndex] === 'meals' && mealsSort}
          {commonMenuContent}

          <div>
            <TabPanels>
              <div ref={containerRef} className="grid overflow-hidden">
                <AnimatePresence initial={false}>
                  {tabs[selectedIndex] === 'meals' && (
                    <motion.div
                      key="meals"
                      animate={{
                        x: 0,
                      }}
                      className="col-start-1 row-start-1"
                      exit={{
                        x: -containerWidth,
                      }}
                      initial={{
                        x: -containerWidth,
                      }}
                      transition={{
                        x: { duration: 0.5, type: 'spring', bounce: 0 },
                      }}
                    >
                      <TabPanel static>{menuTabPanels.meals}</TabPanel>
                    </motion.div>
                  )}

                  {tabs[selectedIndex] === 'extras' && (
                    <motion.div
                      key="extras"
                      animate={{
                        x: 0,
                      }}
                      className="col-start-1 row-start-1"
                      exit={{
                        x: containerWidth,
                      }}
                      initial={{
                        x: containerWidth,
                      }}
                      transition={{
                        x: { duration: 0.5, type: 'spring', bounce: 0 },
                      }}
                    >
                      <TabPanel static>{menuTabPanels.extras}</TabPanel>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </TabPanels>
          </div>
        </div>
      </div>
    </TabGroup>
  )
}

export default MenuTabGroup

const AnimatedTabList = ({
  isHeaderSticky,
  menuTabs,
  totalListingSelections,
}: {
  isHeaderSticky: boolean
  menuTabs: MenuTabs
  totalListingSelections?: number
}) => {
  const { isVisible, selectedIndex, setIsVisible, tabs } = menuTabs
  const selectedMenuTab = tabs[selectedIndex]

  useEffect(() => {
    setIsVisible(!isHeaderSticky)
  }, [isHeaderSticky, setIsVisible])

  return (
    <motion.div
      animate={isVisible ? 'open' : 'closed'}
      className="absolute w-full bg-grey-0"
      initial="open"
      variants={{
        open: {
          transform: 'translateY(0%)',
          transition: { duration: 0.35, type: 'spring', bounce: 0 },
        },
        closed: {
          transform: 'translateY(-100%)',
          transition: { duration: 0.35, type: 'spring', bounce: 0 },
        },
      }}
    >
      <div className="mx-auto max-w-menu">
        <TabList>
          {tabs.map((tab) => {
            return (
              <Tab key={tab}>
                <div className="relative">
                  <div className="flex items-center justify-center py-3">
                    <div className="mr-1.5 inline h-8 w-8 align-middle">
                      {TAB_ICONS[tab]}
                    </div>
                    <div className="flex-start relative flex text-k/18_120">
                      <span className="first-letter:capitalize">{tab}</span>
                      {tab === 'extras' &&
                      totalListingSelections &&
                      totalListingSelections > 0 ? (
                        <div className="relative -top-3 right-0 ml-1 flex h-6 w-6 items-center justify-center rounded-full bg-orange-1 text-k/16_125 text-white">
                          {totalListingSelections}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {tab === selectedMenuTab ? (
                    <motion.div
                      className="absolute bottom-[-1px] left-0 right-0 z-0 h-[3px] bg-orange-1"
                      layoutId="underline"
                      style={{ originY: 'top' }}
                    />
                  ) : null}
                </div>
              </Tab>
            )
          })}
        </TabList>
      </div>
    </motion.div>
  )
}

export interface MenuTabs {
  isVisible: boolean
  selectedIndex: number
  selectTab(tab: Tab): void
  setIsVisible(isVisible: boolean): void
  tabs: Tab[]
}

export const useMenuTabs = (): MenuTabs => {
  const scrollDirection = useScrollDirection()
  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')

  const [scrollY, positionY] = useVariantByScreenSize<[number, number]>(
    [80, 79],
    {
      md: [64, 63],
    },
  )

  const tabs: Tab[] = ['meals', 'extras']
  const defaultTab = tabs.find((tab) => tab === tabParam)

  const [selectedIndex, setSelectedIndex] = useState(
    defaultTab ? tabs.indexOf(defaultTab) : 0,
  )

  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    if (scrollDirection === 'up') {
      setIsVisible(true)
    } else if (scrollDirection === 'down') {
      setIsVisible(false)
    }
  }, [scrollDirection, positionY])

  const selectTab = (tab: Tab) => {
    setIsVisible(true)

    const tabIndex = tabs.indexOf(tab)
    setSelectedIndex(tabIndex)

    if (window.scrollY > scrollY) {
      // Reset scroll position to top of meals/extras grid if scrolled enough that header is sticky
      window.scrollTo(0, positionY)
    }
  }

  return {
    isVisible,
    selectedIndex,
    selectTab,
    setIsVisible,
    tabs,
  }
}
